import { InputField } from 'fields/base/InputField/InputField';
import { useCallback, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { ApiCexPairs, ApiPairs } from 'api';
import { ROUTE_PATHS } from 'shared/consts/routes';
import { useTypedDispatch, useTypedSelector } from 'store';
import { addNewPairToProject } from 'store/slices/projects';
import { IProject } from 'types/project';
import { Button } from 'ui/Button/Button';
import { ButtonLoading } from 'ui/Button/ButtonLoading';
import { ErrorText } from 'ui/ErrorText/ErrorText';
import { ProjectForm } from 'ui/forms';
import { Modal } from 'ui/Modal/Modal';
import { EExchange } from 'web3';

type Props = {
  projectId: number;
  onClose: () => void;
};

function normalizeSymbol(value: string) {
  return value.toUpperCase().replace(/-/g, '/');
}

export function findExistingCexPair(
  projects: IProject[] | null | undefined,
  cex: string,
  base: string,
  quote: string,
) {
  const want = normalizeSymbol(`${base}/${quote}`);
  const cexName = cex.toLowerCase();
  for (const project of projects ?? []) {
    for (const pair of project.cexPairs ?? []) {
      if (String(pair.cex).toLowerCase() === cexName && normalizeSymbol(pair.symbol) === want) {
        return { projectId: project.id, pairId: pair.id };
      }
    }
  }
  return null;
}

export function DSCoverAddPairModal({ projectId, onClose }: Props) {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();
  const projects = useTypedSelector(store => store.projects.projects);
  const [spotCex, setSpotCex] = useState('gate');
  const [spotBase, setSpotBase] = useState('DEXE');
  const [spotQuote, setSpotQuote] = useState('USDT');
  const [perpCex, setPerpCex] = useState('gate');
  const [perpBase, setPerpBase] = useState('DEXE');
  const [perpQuote, setPerpQuote] = useState('USDT');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const onCreate = useCallback(async () => {
    const cex = spotCex.trim();
    const base = spotBase.trim();
    const quote = spotQuote.trim();
    const pCex = perpCex.trim();
    const pBase = perpBase.trim();
    const pQuote = perpQuote.trim();
    if (!cex || !base || !quote || !pCex || !pBase || !pQuote) {
      setError('Fill spot and perp pair');
      return;
    }

    setLoading(true);
    setError(undefined);
    const existingFirst = findExistingCexPair(projects, cex, base, quote);
    if (existingFirst) {
      setLoading(false);
      onClose();
      navigate(
        generatePath(ROUTE_PATHS.cexPair, {
          projectId: String(existingFirst.projectId),
          pairId: String(existingFirst.pairId),
        }),
      );
      return;
    }
    const perpSymbol = `${pBase}_${pQuote}`;
    const created = await ApiPairs.addPair({
      id: projectId,
      cex,
      dex: 'uniswap_v2',
      tradingType: EExchange.cex,
      token_base: base,
      token_quote: quote,
      notes: `ds-cover;spot=${cex}:${base}/${quote};perp=${pCex}:${perpSymbol}`,
      swap_gas_limit: 1000000,
    });
    if (!created.isSuccess) {
      const alreadyExists = (created.errorMessage || '').toLowerCase().includes('already exists');
      const existing = alreadyExists ? findExistingCexPair(projects, cex, base, quote) : null;
      setLoading(false);
      if (existing) {
        onClose();
        navigate(
          generatePath(ROUTE_PATHS.cexPair, {
            projectId: String(existing.projectId),
            pairId: String(existing.pairId),
          }),
        );
        return;
      }
      setError(created.errorMessage || 'Failed to add pair');
      return;
    }

    const pairId = Number(created.data.id);
    const fetched = await ApiCexPairs.getCexPairById(pairId);
    if (fetched.isSuccess) {
      dispatch(addNewPairToProject({ pair: fetched.data, type: EExchange.cex }));
    }

    setLoading(false);
    onClose();
    navigate(
      generatePath(ROUTE_PATHS.cexPair, {
        projectId: String(projectId),
        pairId: String(pairId),
      }),
    );
  }, [
    dispatch,
    navigate,
    onClose,
    perpBase,
    perpCex,
    perpQuote,
    projectId,
    projects,
    spotBase,
    spotCex,
    spotQuote,
  ]);

  return (
    <Modal title="Spot + perp pair" onClose={onClose}>
      <ProjectForm>
        <InputField
          label="Spot CEX"
          type="text"
          value={spotCex}
          setValue={setSpotCex}
          disabled={loading}
        />
        <InputField
          label="Spot base"
          type="text"
          value={spotBase}
          setValue={setSpotBase}
          disabled={loading}
        />
        <InputField
          label="Spot quote"
          type="text"
          value={spotQuote}
          setValue={setSpotQuote}
          disabled={loading}
        />
        <InputField
          label="Perp CEX"
          type="text"
          value={perpCex}
          setValue={setPerpCex}
          disabled={loading}
        />
        <InputField
          label="Perp base"
          type="text"
          value={perpBase}
          setValue={setPerpBase}
          disabled={loading}
        />
        <InputField
          label="Perp quote"
          type="text"
          value={perpQuote}
          setValue={setPerpQuote}
          disabled={loading}
        />
        <ErrorText>{error}</ErrorText>
        <div className="mt-2">
          {!loading && (
            <Button type="button" onClick={() => void onCreate()}>
              Create
            </Button>
          )}
          {loading && <ButtonLoading />}
        </div>
      </ProjectForm>
    </Modal>
  );
}
